import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { askRowan } from "@/components/RowanTerm";
import { ArrowRight, CalendarBlank, CheckCircle, Sparkle, Warning } from "@phosphor-icons/react";

const SEV = { high: "var(--bk-energy)", medium: "#E08A00", low: "var(--bk-accent)" };
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

export function HomeBrief({ businessId, onGoToView }) {
  const [brief, setBrief] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/businesses/${businessId}/home-brief`).then((r) => live && setBrief(r.data)).catch(() => live && setBrief({ headline: "", priorities: [], deadlines: [] }));
    return () => { live = false; };
  }, [businessId]);
  if (!brief) return <div className="bk-card p-5 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="home-brief-loading">Checking what needs you today…</div>;

  const explain = (p) => askRowan(`On my Home screen you flagged: "${p.title}". ${p.detail} Why does this matter for my business and what exactly should I do first?`, p.title);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="home-brief">
      <div className="bk-card p-5 lg:col-span-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-gold)" }}>Today's priorities</p>
            <p className="font-display text-lg font-bold mt-1" style={{ color: "var(--bk-text)" }} data-testid="home-brief-headline">{brief.headline}</p>
          </div>
          {brief.priorities.length === 0 && <CheckCircle size={28} weight="fill" color="var(--bk-success)" />}
        </div>
        {brief.priorities.length > 0 && (
          <ol className="mt-4 space-y-2" data-testid="home-priorities">
            {brief.priorities.map((p, i) => (
              <li key={p.key} className="rounded-xl border p-3 flex flex-wrap items-center gap-3" style={{ borderColor: "var(--bk-border)" }} data-testid={`priority-${i}`}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold" style={{ background: SEV[p.severity] }}>{i + 1}</span>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{p.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{p.detail}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => explain(p)} data-testid={`priority-why-${i}`} className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full hover:bg-[#F6EEDF]" style={{ color: "var(--bk-accent)" }}><Sparkle size={12} weight="fill" color="var(--bk-gold)" /> Why?</button>
                  <button onClick={() => onGoToView(p.tab)} data-testid={`priority-action-${i}`} className="bk-btn-primary text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">{p.action_label} <ArrowRight size={12} weight="bold" /></button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
      <div className="bk-card p-5">
        <p className="text-xs uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--bk-accent)" }}><CalendarBlank size={14} weight="fill" /> Upcoming deadlines</p>
        {brief.deadlines.length === 0 ? (
          <p className="text-sm mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="deadlines-empty">Nothing due in the next few weeks.</p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="home-deadlines">
            {brief.deadlines.map((d, i) => (
              <li key={`${d.kind}-${d.date}-${i}`}>
                <button onClick={() => onGoToView(d.tab)} data-testid={`deadline-${i}`} className="w-full text-left flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-[#F8F7F4]">
                  <span className="text-xs font-mono font-semibold w-14 shrink-0" style={{ color: d.days_left <= 7 ? "var(--bk-energy)" : "var(--bk-text-2)" }}>{fmtDate(d.date)}</span>
                  <span className="text-sm flex-1" style={{ color: "var(--bk-text)" }}>{d.title}</span>
                  {d.days_left <= 3 && <Warning size={14} weight="fill" color="var(--bk-energy)" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
